// src/components/ReportDownloader.jsx
import React, { useRef, useState, useEffect } from 'react';
import styled, { keyframes } from 'styled-components';
import DownloadIcon from '@mui/icons-material/Download';
import html2canvas from 'html2canvas';
import { jsPDF } from 'jspdf';
import QRCode from 'qrcode';
import { useApp } from '../contexts/AppContext';
import { apiUrl } from '../config';

const fadeIn = keyframes`
  from { opacity: 0; transform: translateY(15px); }
  to { opacity: 1; transform: translateY(0); }
`;

const Button = styled.button`
  background: linear-gradient(135deg, #3B82F6, #6366F1);
  color: white;
  padding: 15px 32px;
  font-size: 15px;
  font-weight: 600;
  border: none;
  border-radius: 50px;
  cursor: pointer;
  display: inline-flex;
  align-items: center;
  gap: 10px;
  transition: all 0.3s ease;
  box-shadow: 0 8px 20px rgba(59, 130, 246, 0.3);
  width: 100%;
  justify-content: center;
  margin-top: 20px;
  min-height: 52px;

  &:hover {
    transform: translateY(-2px);
    box-shadow: 0 12px 25px rgba(59, 130, 246, 0.4);
  }

  &:active { transform: translateY(0); }

  &:disabled {
    opacity: 0.7;
    transform: none;
    cursor: not-allowed;
  }

  &:focus-visible { outline: 2px solid #60A5FA; outline-offset: 3px; }

  @media(max-width:480px){
    font-size: 14px;
    padding: 13px 20px;
    border-radius: 14px;
    min-height: 48px;
  }
`;

/* Ширина фиксирована (900px), высота — естественная, по содержимому.
   Центровка на странице A4 делается точным расчётом при сборке PDF (см. downloadReport),
   а не CSS-подгонкой высоты — html2canvas ненадёжно считает height:100%/flex-центровку
   для элементов, отрендеренных за пределами экрана. */
const HiddenTemplate = styled.div`
  position: fixed;
  top: -9999px;
  left: -9999px;
  width: 900px;
  background: white;
  font-family: 'Inter', 'Times New Roman', 'Arial', sans-serif;
  color: #1E293B;
`;

const Frame = styled.div`
  position: relative;
  margin: 18px;
  padding: 38px 52px 30px;
  border: 3px solid #3B82F6;
  box-shadow:
    inset 0 0 0 6px #FFFFFF,
    inset 0 0 0 7px #C9A227,
    inset 0 0 0 11px #FFFFFF,
    inset 0 0 0 12px #3B82F6;
`;

const Watermark = styled.div`
  position: absolute;
  inset: 0;
  display: flex;
  align-items: center;
  justify-content: center;
  transform: rotate(-30deg);
  font-family: Georgia, 'Times New Roman', serif;
  font-size: 100px;
  font-weight: 800;
  color: #3B82F6;
  opacity: 0.04;
  letter-spacing: 8px;
  white-space: nowrap;
  pointer-events: none;
  z-index: 0;
`;

const Content = styled.div`
  position: relative;
  z-index: 1;
  line-height: 1.45;
`;

const TricolorBar = styled.div`
  display: flex;
  height: 6px;
  margin-bottom: 24px;
  border-radius: 3px;
  overflow: hidden;
`;

const WhiteBar = styled.div` flex: 1; background: #FFFFFF; border: 1px solid #E2E8F0; `;
const BlueBar  = styled.div` flex: 1; background: #0039A6; `;
const RedBar   = styled.div` flex: 1; background: #D52B1E; `;

const LogoSection = styled.div`
  display: flex;
  justify-content: space-between;
  align-items: center;
  margin-bottom: 20px;
`;

const Logo = styled.div`
  display: flex;
  align-items: center;
  gap: 12px;
`;

const LogoIcon = styled.div`
  width: 50px;
  height: 50px;
  background: linear-gradient(135deg, #3B82F6, #6366F1);
  border-radius: 12px;
  display: flex;
  align-items: center;
  justify-content: center;
  color: white;
  font-size: 25px;
  font-weight: 700;
  box-shadow: 0 3px 10px rgba(59, 130, 246, 0.35);

  &::before {
    content: '✓';
  }
`;

const LogoText = styled.div`
  font-size: 19px;
  font-weight: 700;
  color: #1E293B;

  span {
    color: #D52B1E;
    font-weight: 500;
  }
`;

const EmblemRight = styled.div`
  text-align: right;
  font-size: 10px;
  color: #64748B;
  line-height: 1.3;
`;

const DocumentTitle = styled.div`
  text-align: center;
  margin: 14px 0 28px 0;
`;

const Title = styled.h1`
  font-family: Georgia, 'Times New Roman', serif;
  font-size: 30px;
  font-weight: 700;
  color: #3B82F6;
  margin: 0 0 12px 0;
  letter-spacing: 2px;
  text-transform: uppercase;
`;

const Ornament = styled.div`
  display: flex;
  align-items: center;
  justify-content: center;
  gap: 12px;
  margin: 0 auto 14px;
  width: 260px;
`;

const OrnamentLine = styled.div`
  flex: 1;
  height: 1.5px;
  background: linear-gradient(90deg, transparent, #C9A227);
  &:last-child { background: linear-gradient(90deg, #C9A227, transparent); }
`;

const OrnamentMark = styled.div`
  color: #C9A227;
  font-size: 15px;
  line-height: 1;
`;

const Subtitle = styled.div`
  font-family: Georgia, 'Times New Roman', serif;
  font-size: 14px;
  color: #475569;
  font-weight: 500;
  font-style: italic;
`;

const DocNumber = styled.div`
  font-size: 11px;
  font-family: monospace;
  margin-top: 12px;
  padding: 5px 14px;
  background: #F1F5F9;
  display: inline-block;
  border-radius: 20px;
  letter-spacing: 0.5px;
`;

const InfoGrid = styled.div`
  display: grid;
  grid-template-columns: 1fr 1fr;
  gap: 14px 40px;
  margin-bottom: 28px;
  font-size: 12px;
`;

const InfoItem = styled.div`
  display: flex;
  justify-content: space-between;
  padding-bottom: 9px;
  border-bottom: 1px solid #E2E8F0;
`;

const Label = styled.span`
  font-weight: 600;
  color: #334155;
`;

const Value = styled.span`
  font-weight: 500;
  color: #1E293B;
`;

const Section = styled.div`
  margin-bottom: 26px;
`;

const SectionTitle = styled.h2`
  font-family: Georgia, 'Times New Roman', serif;
  font-size: 15px;
  font-weight: 700;
  color: #1E293B;
  border-bottom: 2px solid #3B82F6;
  padding-bottom: 7px;
  margin-bottom: 14px;
  text-transform: uppercase;
  letter-spacing: 0.5px;
`;

const RemarksList = styled.div`
  display: flex;
  flex-direction: column;
  gap: 10px;
`;

const RemarkItem = styled.div`
  padding: 10px 14px;
  background: #FFFBEB;
  border-left: 3px solid #C9A227;
  border-radius: 8px;
  font-size: 11.5px;
`;

const RemarkCriterion = styled.div`
  font-weight: 700;
  color: #3B82F6;
  margin-bottom: 3px;
  font-size: 10.5px;
  text-transform: uppercase;
  letter-spacing: 0.3px;
`;

const RemarkText = styled.div`
  color: #334155;
`;

const RemarkReco = styled.div`
  color: #64748B;
  margin-top: 4px;
  font-style: italic;
`;

const RemarksEmpty = styled.div`
  padding: 14px;
  background: #F0FDF4;
  border-left: 3px solid #10B981;
  border-radius: 8px;
  font-size: 12px;
  color: #166534;
  margin-bottom: 26px;
`;

const ResultsTable = styled.table`
  width: 100%;
  border-collapse: collapse;
  margin: 12px 0;
  font-size: 12.5px;
`;

const TableHeader = styled.th`
  background: #3B82F6;
  color: #FFFFFF;
  padding: 10px 14px;
  text-align: left;
  font-weight: 600;
  letter-spacing: 0.3px;
`;

const TableCell = styled.td`
  padding: 10px 14px;
  border-bottom: 1px solid #E2E8F0;
`;

const TotalSection = styled.div`
  background: linear-gradient(135deg, #3B82F6, #6366F1);
  color: white;
  padding: 22px;
  border-radius: 16px;
  text-align: center;
  margin: 8px 0 24px;
  border-top: 3px solid #C9A227;
`;

const TotalScore = styled.div`
  font-family: Georgia, 'Times New Roman', serif;
  font-size: 42px;
  font-weight: 800;
  margin: 6px 0;
`;

const Grade = styled.div`
  font-size: 16px;
  font-weight: 600;
  opacity: 0.95;
  letter-spacing: 0.5px;
`;

const SignaturesSection = styled.div`
  display: flex;
  align-items: flex-end;
  justify-content: space-between;
  gap: 20px;
`;

const SignatureLine = styled.div`
  flex: 1;
  display: flex;
  align-items: center;
  gap: 12px;
  padding-bottom: 8px;
`;

const SignatureRule = styled.div`
  border-top: 1px solid #94A3B8;
  padding-top: 6px;
  font-size: 10px;
  color: #64748B;
`;

const QRImage = styled.img`
  width: 58px;
  height: 58px;
  flex-shrink: 0;
  border: 1px solid #E2E8F0;
  border-radius: 6px;
  padding: 3px;
  background: white;
`;

const StampWrapper = styled.div`
  flex-shrink: 0;
`;

const Stamp = styled.div`
  width: 130px;
  height: 130px;
  border-radius: 50%;
  background: transparent;
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  text-align: center;
  position: relative;

  &::before {
    content: '';
    position: absolute;
    top: 0;
    left: 0;
    right: 0;
    bottom: 0;
    border-radius: 50%;
    border: 2px solid #3B82F6;
    box-shadow: 0 0 0 1px #FFFFFF, 0 0 0 3px #3B82F6, 0 0 0 5px #FFFFFF, 0 0 0 6px #C9A227;
  }

  &::after {
    content: '';
    position: absolute;
    top: 12px;
    left: 12px;
    right: 12px;
    bottom: 12px;
    border-radius: 50%;
    border: 0.5px dashed #3B82F6;
    opacity: 0.5;
  }
`;

const StampTopText = styled.div`
  font-size: 7px;
  font-weight: 600;
  color: #3B82F6;
  text-transform: uppercase;
  letter-spacing: 0.8px;
  margin-top: 14px;
  position: relative;
  z-index: 1;
`;

const StampMainText = styled.div`
  font-size: 11px;
  font-weight: 800;
  color: #3B82F6;
  text-transform: uppercase;
  letter-spacing: 1px;
  margin: 6px 0;
  padding: 3px 0;
  position: relative;
  z-index: 1;
  border-top: 1px solid #3B82F6;
  border-bottom: 1px solid #3B82F6;
`;

const StampBottomText = styled.div`
  font-size: 6px;
  font-weight: 500;
  color: #3B82F6;
  text-transform: uppercase;
  letter-spacing: 0.5px;
  position: relative;
  z-index: 1;
`;

const StampDate = styled.div`
  font-size: 7px;
  font-weight: 500;
  color: #3B82F6;
  margin-top: 4px;
  position: relative;
  z-index: 1;
`;

const Footer = styled.div`
  margin-top: 22px;
  text-align: center;
  font-size: 9px;
  color: #64748B;
  border-top: 1px solid #E2E8F0;
  padding-top: 12px;
`;

// Номер сертификата всегда строится из реального ID записи в базе (та же формула,
// что и на сервере в /verify/:id) — чтобы номер на PDF и номер после сканирования
// QR совпадали. Случайный номер — только запасной вариант для крайне редкого
// случая, когда сохранить результат в базу не удалось (тогда и QR не покажется).
const buildReportNumber = (dateStr, resultId) => {
  const d = dateStr ? new Date(dateStr) : new Date();
  const year  = d.getFullYear();
  const month = String(d.getMonth() + 1).padStart(2, '0');
  const day   = String(d.getDate()).padStart(2, '0');
  const suffix = resultId
    ? resultId.slice(-4).toUpperCase()
    : Math.floor(Math.random() * 9999).toString().padStart(4, '0');
  return `${year}-${month}${day}/${suffix}`;
};

const GRADE_IDX = { excellent: 0, good: 1, satisfactory: 2, poor: 3 };

const ReportDownloader = ({ analysis, fileName }) => {
  const { t, lang } = useApp();
  const reportRef = useRef(null);
  const [isDownloading, setIsDownloading] = React.useState(false);
  const [qrDataUrl, setQrDataUrl] = useState(null);
  const displayFileName = fileName || t.certUnknownFile;

  const dateLocale = lang === 'uz' ? 'uz-UZ' : 'ru-RU';
  // Дата проверки — реальное время анализа (analysis.meta.checkedAt), а не момент
  // скачивания PDF. Иначе если скачать сертификат не сразу, дата на документе
  // разойдётся с датой, которая хранится в базе и показывается на /verify.
  const checkedAtDate = analysis?.meta?.checkedAt ? new Date(analysis.meta.checkedAt) : new Date();
  const currentDate = checkedAtDate.toLocaleDateString(dateLocale, {
    year: 'numeric',
    month: 'long',
    day: 'numeric'
  });
  const reportNumber = buildReportNumber(analysis?.meta?.checkedAt, analysis?.resultId);

  // QR-код ведёт на реальную запись результата в базе — работает, только если
  // сервер сохранил результат (analysis.resultId), а не выдумывает ссылку.
  useEffect(() => {
    if (!analysis?.resultId) { setQrDataUrl(null); return; }
    let cancelled = false;
    QRCode.toDataURL(apiUrl(`/verify/${analysis.resultId}`), {
      width: 160,
      margin: 1,
      color: { dark: '#3B82F6', light: '#FFFFFF' },
    }).then(url => { if (!cancelled) setQrDataUrl(url); }).catch(() => {});
    return () => { cancelled = true; };
  }, [analysis?.resultId]);

  const downloadReport = async () => {
    if (!reportRef.current || isDownloading) return;

    setIsDownloading(true);

    try {
      await new Promise(resolve => setTimeout(resolve, 150));

      const canvas = await html2canvas(reportRef.current, {
        scale: 2.8,
        backgroundColor: '#ffffff',
        logging: false,
        useCORS: true,
        windowWidth: reportRef.current.scrollWidth,
        windowHeight: reportRef.current.scrollHeight
      });

      // JPEG вместо PNG — тот же скриншот на белом фоне без прозрачности,
      // но в 20-30 раз меньше по весу (PNG-версия весила ~26 МБ на один лист).
      const imgData = canvas.toDataURL('image/jpeg', 0.92);

      // Страница всегда стандартный A4. Картинка вписывается в неё с сохранением
      // пропорций и центрируется по обеим осям — если контента меньше страницы,
      // сверху/снизу остаются РАВНЫЕ поля (как у настоящего сертификата), а не
      // пустота в произвольном месте; если контента больше — он вписывается
      // без обрезки и без искажений, а не растягивается CSS-подгонкой.
      const pdf = new jsPDF({ orientation: 'portrait', unit: 'mm', format: 'a4' });
      const pageWidth  = pdf.internal.pageSize.getWidth();
      const pageHeight = pdf.internal.pageSize.getHeight();
      const pageRatio  = pageWidth / pageHeight;
      const imgRatio   = canvas.width / canvas.height;

      let drawWidth, drawHeight;
      if (imgRatio > pageRatio) {
        drawWidth  = pageWidth;
        drawHeight = pageWidth / imgRatio;
      } else {
        drawHeight = pageHeight;
        drawWidth  = pageHeight * imgRatio;
      }
      const offsetX = (pageWidth  - drawWidth)  / 2;
      const offsetY = (pageHeight - drawHeight) / 2;

      pdf.addImage(imgData, 'JPEG', offsetX, offsetY, drawWidth, drawHeight, undefined, 'MEDIUM');
      pdf.save(`${t.certFileName}_MyEduCheck_${reportNumber}.pdf`);
    } catch (error) {
      console.error('Ошибка генерации PDF:', error);
    } finally {
      setIsDownloading(false);
    }
  };

  const getGrade = () => {
    const idx = GRADE_IDX[analysis?.status];
    if (idx !== undefined) return t.grades[idx];
    const total = analysis?.totalScore || 0;
    if (total >= 90) return t.grades[0];
    if (total >= 70) return t.grades[1];
    if (total >= 60) return t.grades[2];
    return t.grades[3];
  };

  const getStatusForStamp = () => {
    if (analysis?.status && t.certStamp[analysis.status]) return t.certStamp[analysis.status];
    const total = analysis?.totalScore || 0;
    if (total >= 80) return t.certStamp.excellent;
    if (total >= 65) return t.certStamp.good;
    if (total >= 50) return t.certStamp.satisfactory;
    return t.certStamp.poor;
  };

  const totalScore = analysis?.totalScore ?? 85;
  const remarks = (analysis?.issues || []).slice(0, 5);

  return (
    <>
      <Button onClick={downloadReport} disabled={isDownloading}>
        <DownloadIcon />
        {isDownloading ? t.certGenerating : t.certDownloadBtn}
      </Button>

      <HiddenTemplate ref={reportRef}>
        <Frame>
          <Watermark>MYEDUCHECK</Watermark>

          <Content>
            <TricolorBar>
              <WhiteBar /><BlueBar /><RedBar />
            </TricolorBar>

            <LogoSection>
              <Logo>
                <LogoIcon />
                <LogoText>My<span>EduCheck</span></LogoText>
              </Logo>
              <EmblemRight>
                {t.certBadge}<br />
                myeducheck.uz
              </EmblemRight>
            </LogoSection>

            <DocumentTitle>
              <Title>{t.certTitle}</Title>
              <Ornament>
                <OrnamentLine /><OrnamentMark>❖</OrnamentMark><OrnamentLine />
              </Ornament>
              <Subtitle>{t.certSubtitle}</Subtitle>
              <DocNumber>№ {reportNumber}</DocNumber>
            </DocumentTitle>

            <InfoGrid>
              <InfoItem><Label>{t.certWorkName}</Label><Value>{displayFileName}</Value></InfoItem>
              <InfoItem><Label>{t.certCheckDate}</Label><Value>{currentDate}</Value></InfoItem>
              <InfoItem><Label>{t.certService}</Label><Value>MyEduCheck</Value></InfoItem>
            </InfoGrid>

            <Section>
              <SectionTitle>{t.certResultsTitle}</SectionTitle>
              <ResultsTable>
                <thead>
                  <tr>
                    <TableHeader>{t.certCriterion}</TableHeader>
                    <TableHeader style={{ textAlign: 'center' }}>{t.certEarned}</TableHeader>
                    <TableHeader style={{ textAlign: 'center' }}>{t.certMax}</TableHeader>
                  </tr>
                </thead>
                <tbody>
                  <tr>
                    <TableCell>{t.certRowFormat}</TableCell>
                    <TableCell style={{ textAlign: 'center', fontWeight: 700 }}>{analysis?.fontTotal ?? 0}</TableCell>
                    <TableCell style={{ textAlign: 'center' }}>15</TableCell>
                  </tr>
                  <tr style={{ background: '#F8FAFC' }}>
                    <TableCell>{t.certRowVolume}</TableCell>
                    <TableCell style={{ textAlign: 'center', fontWeight: 700 }}>{analysis?.volumeTotal ?? 0}</TableCell>
                    <TableCell style={{ textAlign: 'center' }}>20</TableCell>
                  </tr>
                  <tr>
                    <TableCell>{t.certRowStructure}</TableCell>
                    <TableCell style={{ textAlign: 'center', fontWeight: 700 }}>{analysis?.structureTotal ?? 0}</TableCell>
                    <TableCell style={{ textAlign: 'center' }}>50</TableCell>
                  </tr>
                  <tr style={{ background: '#F8FAFC' }}>
                    <TableCell>{t.certRowAnalysis}</TableCell>
                    <TableCell style={{ textAlign: 'center', fontWeight: 700 }}>{analysis?.analysisTotal ?? 0}</TableCell>
                    <TableCell style={{ textAlign: 'center' }}>15</TableCell>
                  </tr>
                  <tr style={{ background: '#EFF6FF', fontWeight: 700 }}>
                    <TableCell><strong>{t.certTotalRow}</strong></TableCell>
                    <TableCell style={{ textAlign: 'center', fontWeight: 800, fontSize: 14 }}><strong>{analysis?.totalScore ?? 0}</strong></TableCell>
                    <TableCell style={{ textAlign: 'center', fontWeight: 800 }}><strong>100</strong></TableCell>
                  </tr>
                </tbody>
              </ResultsTable>
            </Section>

            <TotalSection>
              <div style={{ fontSize: '12px', opacity: 0.85, letterSpacing: '1px' }}>{t.certTotalScore}</div>
              <TotalScore>{totalScore} / 100</TotalScore>
              <Grade>{getGrade()}</Grade>
            </TotalSection>

            {remarks.length > 0 ? (
              <Section>
                <SectionTitle>{t.certRemarksTitle}</SectionTitle>
                <RemarksList>
                  {remarks.map((r, i) => (
                    <RemarkItem key={i}>
                      <RemarkCriterion>{r.criterion}</RemarkCriterion>
                      <RemarkText>{r.issue}</RemarkText>
                      {r.recommendation && <RemarkReco>→ {r.recommendation}</RemarkReco>}
                    </RemarkItem>
                  ))}
                </RemarksList>
              </Section>
            ) : (
              <RemarksEmpty>{t.certNoRemarks}</RemarksEmpty>
            )}

            <SignaturesSection>
              <SignatureLine>
                {qrDataUrl && <QRImage src={qrDataUrl} alt="QR" />}
                <div>
                  {qrDataUrl && <div style={{ fontSize: 9, color: '#64748B', marginBottom: 4 }}>{t.certScanToVerify}</div>}
                  <SignatureRule>{t.certSignatureLine}</SignatureRule>
                </div>
              </SignatureLine>
              <StampWrapper>
                <Stamp>
                  <StampTopText>• MYEDUCHECK •</StampTopText>
                  <StampMainText>{getStatusForStamp()}</StampMainText>
                  <StampBottomText>myeducheck.uz</StampBottomText>
                  <StampDate>{currentDate}</StampDate>
                </Stamp>
              </StampWrapper>
            </SignaturesSection>

            <Footer>
              {t.certFooter1} • {new Date().toLocaleString(dateLocale)} •
              MyEduCheck © 2026 • {t.certFooter2}
            </Footer>
          </Content>
        </Frame>
      </HiddenTemplate>
    </>
  );
};

export default ReportDownloader;
